import React from "react";
import { Link } from "react-router";
import { ToastContainer } from "react-toastify";

import SimpleHeader from "@/components/Headers/SimpleHeader";
import TableWrapper from "@/utils/TableWrapper";
import type { SitePermission } from "@/services/userServices/useCurrentPermission";

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Row,
} from "reactstrap";

/** A product-properties row of the list, as the columns below read it. */
type ProductPropertiesRow = {
  _id: string;
  product: { productImg?: string; title?: string; country_code?: string };
  subcategory?: { name?: string; category?: { name?: string } | null } | null;
  properties: unknown[];
};

type ProductPropertiesCellProps = { row: { original: ProductPropertiesRow } };

export type ManageProductPropertiesViewProps = {
  data?: ProductPropertiesRow[];
  permission?: Readonly<SitePermission>;
  alertNode?: React.ReactNode;
  onRequestDelete: (id: string) => void;
};

const ManageProductPropertiesView = ({
  data = [],
  permission = {},
  alertNode,
  onRequestDelete,
}: ManageProductPropertiesViewProps) => {

  const columns = [
    {
      header: "Logo",
      cell: ({ row }: ProductPropertiesCellProps) => (
        <img
          className="avatar rounded-circle mr-3"
          src={row.original.product.productImg}
          alt="avatar"
        />
      ),
    },
    {
      accessorFn: (row: ProductPropertiesRow) => row.product.title,
      header: "Title",
    },
    {
      accessorFn: (row: ProductPropertiesRow) => row.subcategory?.category?.name ?? "—",
      header: "Category",
    },
    {
      accessorFn: (row: ProductPropertiesRow) => row.subcategory?.name ?? "—",
      header: "Subcategory",
    },
    {
      accessorFn: (row: ProductPropertiesRow) => row.product.country_code,
      header: "Country Code",
    },
    {
      header: "Properties",
      cell: ({ row }: ProductPropertiesCellProps) => (
        <div>
          <Button className="btn-icon-only rounded-circle" color="facebook" disabled>
            {row.original.properties.length}
          </Button>
        </div>
      ),
    },
    {
      id: "actions",
      header: "ACTION",
      accessorKey: "link",
      cell: ({ row }: ProductPropertiesCellProps) => (
        <div>
          <Button
            className="btn-round btn-icon"
            color="primary"
            to={`/admin/updateproductproperties/${row.original._id}`}
            tag={Link}
            size="sm"
          >
            <span className="btn-inner--icon mr-1">
              <i className="ni ni-settings-gear-65" />
            </span>
            <span className="btn-inner--text">Details</span>
          </Button>
          {(permission.publish || permission.edit) && (
            <Button
              className="btn-round btn-icon"
              color="danger"
              onClick={() => onRequestDelete(row.original._id)}
              size="sm"
            >
              <span className="btn-inner--icon mr-1">
                <i className="fas fa-trash" />
              </span>
              <span className="btn-inner--text">Delete</span>
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Notifications */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      {/* SweetAlert */}
      {alertNode}

      <SimpleHeader name="Manage Product Properties" parentName="Product" />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader>
            <Row>
              <Col xs="8" >
                <h5 className="h3 mb-0">All Product Properties</h5>
                This listing contains all the products that already have properties,
                filtered by the current website country code
                <br />
                You can set values for any property by clicking on the Details button
                <br />
                You can remove properties from a product by clicking on the Delete button
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <TableWrapper data={data} columns={columns} />
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default ManageProductPropertiesView;
